import React, { useState, useEffect, useRef } from "react";
import useAxios from "../../hooks/useAxios";
import useCommonActions from "../../hooks/useCommonActions";
import badges from "../../_helpers/badgeHelper";
import helpers from "../../_helpers/common";
import { hasAuthorization } from "../../_helpers/hasAuthorization";
import { ContentLoader, PageToolbar, Pagination, TooltipWrapper } from "../common";
import SearchForm from "./partial/SearchForm";
import CreateUpdateAdmin from "./CreateUpdateAdmin";
import CommonList from "../common/CommonList";
import { UserSearchFormState, User } from "../../types/interfaces";


type UserListProps = {
  role_id: number;
};
const List: React.FC<UserListProps> = ({ role_id }) => {
  //console.log(role_id);
  const axiosInstance = useAxios();
  const { changeApprovalStatus, changeItemStatus, deleteItem } = useCommonActions();
  const [searchForm, setSearchForm] = useState<UserSearchFormState>({ search_string: "", status: "" });
  const [results, setResults] = useState<User[] | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10 });
  const [pagetitle, setPageTitle] = useState("Users List");
  const [modalopen, setModalOpen] = useState(false);
  const [selecteduser, setSelectedUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isInitialRender = useRef(false);

  /*Handle pagination data change*/
  const handlePageChange = (page: number, pageSize: number) => {
    setPagination({ currentPage: page, pageSize });
  };

  const getListData = React.useCallback(async () => {
	  setIsLoading(true);
	  try {
		const response: any = await axiosInstance({
		  url: "users/users-list",
		  method: "GET",
		  data: {
			role_id,
			page: pagination.currentPage,
			limit: pagination.pageSize,
			...searchForm,
		  },
		});

		setResults(response.data.users.docs);
		setTotalItems(response.data.users.totalDocs);
	  } catch (error) {
		console.error(error);
	  } finally {
		setTimeout(() => setIsLoading(false), 500);
	  }
  }, [role_id, pagination, searchForm, axiosInstance]);


  useEffect(() => {
	  // update page title
	  if (role_id === 2) {
		setPageTitle("Customers List");
	  } else if (role_id === 3) {
		setPageTitle("Pandit List");
	  } else {
		setPageTitle("Admin Users List");
	  }

	  // reset page when role changes
	  setPagination((prev) => ({ ...prev, currentPage: 1 }));

	  getListData();
  }, [role_id, pagination.currentPage, pagination.pageSize]);
  
  useEffect(() => {
	if(!modalopen){
		setSelectedUser(null);
	}
  }, [modalopen])
  
  const getNotificationColor = (item: User | null) => {
    if (item?.is_logged_in) {
      return "bg-success";
    } else {
      return "bg-danger";
    }
  };

  /*Change selected item status*/
  const changeAdminStatus = async (userid?: string, status?: number) => {
    const newStatus = status === 1 ? 2 : 1;
    const message = `Are you sure you want to ${status === 1 ? "block" : "unblock"} the user?`;
    changeItemStatus(
      userid,
      newStatus,
      "users/update-user-status",
      message,
      getListData
    );
  };

  /*Delete selected item*/
  const deleteAdmin = async (itemid?: string) => {
    const message = `Are you sure you want to delete the user?`;
    deleteItem(`users/delete-student/${itemid}`, message, getListData);
  };
  
   /*Change Admin approval status*/
  const updateApprovalStatus = async (
    itemId?: string,
    approvalstatusname?: string
  ) => {
    changeApprovalStatus({
      itemId,
      url: "users/update-approval-status",
      selectedOption: approvalstatusname,
      callback: getListData,
    });
  };
  
  const handleEditUser = (selecteduser: User) => {
	  setSelectedUser(selecteduser);
	  setModalOpen(true);
  }
  
  return (
    <>
      <div className="d-flex justify-content-between">
        {/*Page toolbar Area Start*/}
        <PageToolbar title={pagetitle} />
		 <button
			className="btn btn-flex btn-primary h-40px fs-7 fw-bold"
			onClick={() => setModalOpen(true)}
		  >
			Create Admin
		 </button>
      </div>
	  <div id="kt_content" className="content flex-column-fluid">
        {/*Search Area Start*/}
        <SearchForm searchForm={searchForm} setSearchForm={setSearchForm} handleSearchClick={getListData}/>
        {/*Search Area End*/}

        {/*Content Area Start*/}
        <div className={`card ${isLoading ? "overlay overlay-block" : ""}`}  style={{ minHeight:"70vh", maxHeight: "70vh" }} >
          <div className="card-body pt-0 pb-0 table-responsive">
		     <table className="table table-hover table-row-bordered table-row-solid gy-4 align-middle fw-bold w-100">
			  <thead
				style={{
				  position: "sticky",
				  top: 0,
				  zIndex: 5,
				  backgroundColor: `${isLoading ? "#BBBCBE" : "white"}`,
				  boxShadow: "0 4px 2px -2px rgba(190, 190, 190, 0.2)",
				}}
			  >
				<tr className="text-start text-gray-800 fw-bold fs-7 text-uppercase gs-0">
				  <th className="min-w-200px">Name</th>
				  {(role_id === 0 || role_id === 1) && (
					  <th className="min-w-200px">Role</th>
				  )}
				  <th className="min-w-200px">Email</th>
				  <th className="min-w-200px">Phone No</th>
				  <th className="min-w-70px">Status</th>
				  <th className="min-w-70px">Approval Status</th>
				  <th className="min-w-150px">Created Date</th>
				  <th className="min-w-70px">Actions</th>
				</tr>
			  </thead>
			  <tbody className="fw-semibold text-gray-600">
				{!results || results.length === 0 ? (
				  <tr className="text-center">
					<td colSpan={10}>No Content Found</td>
				  </tr>
				) : (
				  results.map((item) => (
					<tr key={item._id}>
					  <td>
						<div className="d-flex align-items-center">
						  <span className={`bullet bullet-dot me-5 w-8px h-8px ${getNotificationColor(item)}`}></span>
						  <div className="symbol symbol-circle symbol-40px overflow-hidden me-3">
							<div className="symbol-label">
							  <img
								src={item.imagepath}
								alt={item.fullname}
								className="w-100 h-100"
							  />
							</div>
						  </div>
						  <div className="d-flex flex-column justify-content-center">
							<div className="fw-semibold fs-6 text-gray-500">
							  {item.fullname || "N/A"}
							</div>
						  </div>
						</div>
					  </td>
					  {(role_id === 0 || role_id === 1) && (
						 <td className="text-uppercase">{item.rolename || "N/A"}</td>
					  )}
					  <td>{item.email || "N/A"}</td>
					  {/* <td>{item.phone_no || "N/A"}</td> */}
					  <td>
						<span>
						  {item?.phone_country_code
							? (item?.phone_country_code).includes("+")
							  ? ""
							  : "+"
							: ""}
						  {item?.phone_country_code} {item?.phone_no || "N/A"}
						</span>
					  </td>
					  <td>
						{badges.userStatusBadge(
						  item?.status ?? 0,
						  item?.statusname ?? ""
						)}
					  </td>
					  <td>
						{badges.adminApprovalBadge(
						  item?.approval_status ?? 0,
						  item?.approvalstatusname ?? ""
						)}
					  </td>
					  <td>
						{helpers.convertDateTime(
						  item?.created_at,
						  "DD MMM YYYY"
						) || "N/A"}
					  </td>
					  <td className="text-center">
						<div className="d-flex">
						  {hasAuthorization("edit", "admin") && (
							<TooltipWrapper title="Edit" placement="top">
							  <span
								className="btn btn-sm btn-icon btn-light btn-active-light-warning me-2"
								onClick={() =>
								  handleEditUser(item)
								}
							  >
								<i className="ki-outline ki-notepad-edit text-warning fs-2"></i>
							  </span>
							</TooltipWrapper>
						  )}
						  {hasAuthorization("approve", "admin") && (
							<TooltipWrapper
							  title={item?.approvalstatusname ?? ""}
							  placement="top"
							>
							  <span
								className="btn btn-sm btn-icon btn-light btn-active-light-grey me-2"
								onClick={() =>
								  updateApprovalStatus(item?._id, item?.approvalstatusname)
								}
							  >
								<i className="ki-outline ki-security-user text-grey fs-2"></i>
							  </span>
							</TooltipWrapper>
						  )}
						  {hasAuthorization("block_unblock", "admin") && (
							<TooltipWrapper
							  title={item.status === 1 ? "Block" : "Unblock"}
							  placement="top"
							>
							  <span
								className="btn btn-sm btn-icon btn-light btn-active-light-warning me-2"
								onClick={() =>
								  changeAdminStatus(item?._id, item?.status)
								}
							  >
								<i className="ki-outline ki-switch text-warning fs-2"></i>
							  </span>
							</TooltipWrapper>
						  )}
						  {hasAuthorization("delete", "admin") && (
							<TooltipWrapper title="Delete Admin" placement="top">
							  <span
								className="btn btn-sm btn-icon btn-light btn-active-light-danger"
								onClick={() => deleteAdmin(item._id)}
							  >
								<i className="ki-outline ki-trash text-danger fs-2"></i>
							  </span>
							</TooltipWrapper>
						  )}
						</div>
					  </td>
					</tr>
				  ))
				)}
			  </tbody>
			  <tfoot></tfoot>
			</table>
		  </div>
          {/*Content Area End*/}

          {/*Pagination Start*/}
          <div
            className={`sticky-bottom bg-white border rounded p-2 shadow-sm ${
              isLoading ? "" : "border-top"
            }`}
            style={{
              bottom: 0,
              zIndex: 2,
              // backgroundColor: `${isLoading ? "#BBBCBE" : "white"}`,
            }}
          >
            {results && results.length > 0 ? (
              <Pagination
                totalItems={totalItems}
                itemsPerPage={pagination.pageSize}
                onPageChange={handlePageChange}
              />
            ) : null}
          </div>
          {/*Pagination End*/}
          {/*Content Loader Start*/}
          {isLoading ? <ContentLoader /> : null}
          {/*Content Loader End*/}
		  
		  { modalopen && (
		    <CreateUpdateAdmin show={modalopen} handleModalOpenClose={setModalOpen} user={selecteduser} getListData={getListData}/>
		  )}
        </div>
      </div>
	</>
  );
};

export default List;